<template lang="pug">
  .main-container
    .rogue-shipping-system-page
      block(name="shipping-delay")
      h1(v-if="!$rogue.appContext") {{ __('Rogue Shipping System') }}
      Modal(
        v-if="message"
        width="max-content"
        :class="!message.success ? 'is-error' : ''"
        @close="message = null"
      )
        span(
          :class="!message.success && 'font-color-red'"
          v-html=`__('There were no orders found based on the Email or Order Number Provided. If you believe this is an error. Please <a href="{contact}">contact us.</a>', {contact: $rogue.l10n.path + '/contact-us'})`
        )
      Spinner.rogue-shipping-system(
        size="l"
        :active="loading"
        :class="`rogue-shipping-system-${landing ? 'landing' : 'order-details'}`"
      )
        landing-page(v-if="!loading && landing")
        order-details(
          v-else-if="!loading"
          :order-data="rssData"
        )
    ProductCarousel.main-container.products(
      v-if="orderItemIds.length"
      id="rss-recommendations"
      :widget="widgets.RSS"
      :podId="PODS.CUSTOMERS_ALSO_BOUGHT"
      :itemIds="orderItemIds"
      title="Customers Also Bought These Products"
    )
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import LandingPage from './subroutes/landing.vue'
import OrderDetails from './subroutes/order-details.vue'
import type { IRssResponse } from './rssTypes'
import { useConfigStore } from '@/js/stores/configStore'
import { PICKUP_CODE } from '@/js/helpers/shipping'
import { widgets } from '@/js/services/rfk/types'
import { PODS } from '@/js/services/cnstrc/constants'
import metadata from '@/js/helpers/metadata'
import block from '@/js/components/dynamic-block.vue'
import { useRogue } from '@/js/services/Rogue'
import { useProductStore } from '@/js/stores/productStore'
import { MSUnit } from '@/js/pages/product/rackbuilder/utils/ms-utils/ms-constants'
import { MMSUnit } from '@/js/pages/product/rackbuilder/utils/mms-utils/mms-constants'
import { isSlingerX } from '@/js/pages/product/rackbuilder/products/rack/functionalTrainer/FM/slinger-x/slingerXUtils'
import { CAVE_SKUS } from '@/js/pages/product/rackbuilder/products/cave/utils/caveConstants'

const DEEP_NESTED_UNIT_SKUS = [...MSUnit, ...MMSUnit]

const initialData = {
  comments     : '',
  order_number : '',
  email        : '',
  is_uspsi     : false,
  apps         : {},
  data         : {
    packages      : [],
    address       : '',
    postalCode    : '',
    sourceOrderId : '',
    country       : '',
    email         : '',
    orderNumber   : '',
  },
}

const configStore = useConfigStore()
const productStore = useProductStore()
const route = useRoute()
const router = useRouter()
const rogue = useRogue()

useHead(metadata({ title: rogue.__('Track your Order'), url: `${rogue.localizedBaseURL}${route.path}` }, rogue))

const loading = ref<boolean>(true)
const rssData = ref<Partial<IRssResponse>>({})
const message = ref<Record<string, unknown> | null>(null)
const orderItemIds = ref<string[]>([])

const landing = computed<boolean>(() => !rssData.value?.data || (!route.query.token && (!route.query.data || !route.query.order)))

let replacingUrl = false

watch(() => [route.query.token, route.query.data, route.query.order], () => {
  if (replacingUrl) {
    replacingUrl = false

    return
  }
  fetchRssOrder()
})

onMounted(async () => {
  if (route.name === 'rss' && !route.query.data && route.query.email) {
    rogue.log.warn('Rogue App - Missing data query parameter, email used instead', route.query)
    await router.replace({
      query: {
        ...route.query,
        data: btoa(decodeURIComponent(route.query.email as string)),
      },
    })
  }

  await fetchRssOrder()
})

// MS/MMS/Cave/Slinger X bundles nest a grandchild (unit -> side panel) whose listItemId a shipment can reference instead of the top-level item's - recurse fully for those, shallow (1-level) check for everything else
function matchesAnyDescendant(item: any, listItemIds: string[]): boolean {
  return listItemIds.includes(item.listItemId)
    || !!item.children?.some((child: any) => matchesAnyDescendant(child, listItemIds))
}

// A Slinger X item's own embedded sku can be stale/dynamic at purchase time, so it's matched via the live catalog product (isSlingerX) instead of item.sku.
function needsDeepShipmentMatch(item: any): boolean {
  return DEEP_NESTED_UNIT_SKUS.includes(item.sku) || CAVE_SKUS.includes(item.sku) || isSlingerX(rogue, item)
}

function itemMatchesShipment(item: any, listItemIds: string[]): boolean {
  if (listItemIds.includes(item.listItemId)) {
    return true
  }

  if (!needsDeepShipmentMatch(item)) {
    return !!item.children?.some((child: any) => listItemIds.includes(child.listItemId))
  }

  return !!item.children?.some((child: any) => matchesAnyDescendant(child, listItemIds))
}

async function fetchRssOrder() {
  loading.value = true
  let { data, order } = route.query as { data?: string; order?: string }
  const token = decodeURIComponent(route.query.token as string || '') || null
  let rssResp: Partial<IRssResponse> = {}
  let orderResponse: any = {}
  let checkoutResp: any = {}
  let response: any = {}
  let rssSuccess = false

  if (!token && (!order || !data)) {
    rssData.value = {}
    message.value = null
    loading.value = false
    
    return
  }

  if (!token) {
    rogue.log.info('Rogue App - Fetching RSS order data', { data, order })
  } else {
    rogue.log.info('Rogue App - Fetching RSS order data with token', { token })
  }

  try {
    let rssPromise: Promise<IRssResponse>
    let orderPromise: Promise<any>
    if (token) {
      rssPromise = rogue.fetch<IRssResponse>(`/svc_rss/rss?token=${encodeURIComponent(token)}`)
      orderPromise = rogue.fetch(`/svc_checkout/order?token=${encodeURIComponent(token)}`)
    } else {
      const email = atob(decodeURIComponent(data!))
      rssPromise = rogue.fetch<IRssResponse>(`/svc_rss/rss?email=${encodeURIComponent(email)}&order=${order}`)
      orderPromise = rogue.fetch(`/svc_checkout/order?email=${encodeURIComponent(email!)}&orderId=${order}`)
    }

    const [rssResult, orderResult] = await Promise.allSettled([rssPromise, orderPromise])

    if (rssResult.status === 'fulfilled') {
      rssResp = rssResult.value
      rssSuccess = rssResp?.success === true
    } else {
      rogue.log.info('Unable to load RSS', rssResult.reason)
      rssSuccess = false
    }

    if (orderResult.status === 'rejected') {
      throw orderResult.reason
    }
    orderResponse = orderResult.value

    // If using legacy data/order params, swap the URL to use the token instead
    if (!token && orderResponse.rssToken) {
      replacingUrl = true
      router.replace({
        query: {
          ...route.query,
          token : encodeURIComponent(orderResponse.rssToken),
          data  : undefined,
          order : undefined,
        },
      })
    }

    const email = rssResp.data?.email
    const { comments, createdAt, items, orderId, shipping, shippingAddress, status, meta } = orderResponse as any
    orderItemIds.value = (items || []).map((i: any) => i.productId).filter(Boolean).map(String)

    const orderCreatedAt = (orderResponse as any).orderCreatedAt || meta?.createdAt || meta?.updatedAt || createdAt

    if (orderCreatedAt && items.length > 0) {
      // Only items with children can reach isSlingerX below (DEEP_NESTED_UNIT_SKUS items short-circuit first) - load catalog data for just that narrow set, not every item in the order.
      const deepMatchCandidateIds = items
        .filter((item: any) => item.children?.length && !DEEP_NESTED_UNIT_SKUS.includes(item.sku))
        .map((item: any) => item.productId)
        .filter(Boolean)

      if (deepMatchCandidateIds.length) {
        await productStore.loadAll(deepMatchCandidateIds, rogue)
      }

      let packages: any[] = []
      const itemsInPackages = new Set<string>()
      const hasPhysicalShipping = shipping && shipping.length > 0

      // Identify virtual items FIRST before building packages
      const digitalProducts = rssResp.data?.digitalProducts
      const digitalSKUs = (digitalProducts?.items?.map(it => it.sku?.toLowerCase()) || []).filter(Boolean)
      const virtualItemIds = new Set(
        items
          .filter((item: any) => 
            item.metadata?.giftcard || 
            (digitalSKUs.length > 0 && item.sku && digitalSKUs.includes(item.sku.toLowerCase()))
          )
          .map((item: any) => item.listItemId)
      )

      if (hasPhysicalShipping) {
        packages = shipping.map((s: any) => {
          const packageItems = items.filter((item: any) => {
            // Exclude virtual items from physical packages
            if (virtualItemIds.has(item.listItemId)) {
              return false
            }
            
            const inShipping = s.shippingCode === PICKUP_CODE
              || itemMatchesShipment(item, s.listItemIds || [])
            if (inShipping) {
              itemsInPackages.add(item.listItemId)
            }
            
            return inShipping
          })

          return {
            items         : packageItems,
            receivedDate  : orderCreatedAt,
            processedDate : orderCreatedAt,
            status        : status === 'published' ? 'received' : status,
            carrier       : s.carrier,
            isPickup      : s.shippingCode === PICKUP_CODE,
            rssSuccess,
          }
        })
      }

      const digitalItems = items.filter((item: any) => (
        virtualItemIds.has(item.listItemId)
        || (!hasPhysicalShipping && !itemsInPackages.has(item.listItemId))
      ))

      if (digitalItems.length > 0) {
        const digitalDeliveryDate = digitalProducts?.deliveryDate || (rssSuccess && status === 'published' ? orderCreatedAt : null)
        const digitalStatus = digitalDeliveryDate ? 'delivered' : (status === 'published' ? 'received' : status)

        packages.push({
          items         : digitalItems,
          receivedDate  : orderCreatedAt,
          processedDate : rssSuccess ? (digitalDeliveryDate || orderCreatedAt) : undefined,
          status        : digitalStatus,
          deliveryDate  : digitalDeliveryDate,
          packageType   : 'virtual',
          rssSuccess,
        })
      }

      if (packages.length === 0) {
        packages = [{ items, receivedDate: orderCreatedAt, processedDate: orderCreatedAt, status: status === 'published' ? 'received' : status, rssSuccess }]
      }

      checkoutResp = {
        comments,
        email,
        order_number : orderId,
        data         : {
          email,
          packages,
          address          : shippingAddress?.street?.join(', '),
          postalCode       : shippingAddress?.postcode,
          sourceOrderId    : orderId,
          country          : configStore.countries.find((c: any) => c.alpha2 === shippingAddress?.countryId)?.name,
          delivery_address : shippingAddress ? {
            firstname : shippingAddress.firstname,
            lastname  : shippingAddress.lastname,
            street_1  : shippingAddress.street?.[0],
            street_2  : shippingAddress.street?.[1],
            city      : shippingAddress.city,
            region    : shippingAddress.region,
            postcode  : shippingAddress.postcode,
            countryId : shippingAddress.countryId,
          } : undefined,
          giftcards: items.filter((it: any) => it.metadata?.giftcard),
        },
        success: orderId,
      }
    }
  } catch (e) {
    rssSuccess = false
    rogue.log.warn('RSS verification failed or no email returned', e as Error)
    if (rssResp && rssResp.data) {
      checkoutResp = {
        data: {
          delivery_address: {
            firstname : '',
            lastname  : '',
            street_1  : rssResp.data.address,
            city      : rssResp.data.country,
            region    : '',
            postcode  : rssResp.data.postalCode,
          },
        },
      }
    }
  }

  // Merge packages: prefer RSS packages (with proper status), but add packageType for virtual packages
  let finalPackages = []
  if (rssResp.data?.packages && rssResp.data.packages.length > 0) {
    // RSS has packages - use them and mark digital/virtual packages
    finalPackages = rssResp.data.packages.map((pkg: any) => {
      if (pkg.shippingMethod === 'DigitalProduct' || (pkg.status === 'delivered' && !pkg.trackingNumber)) {
        return { ...pkg, packageType: 'virtual' }
      }
      
      return pkg
    })
  } else if (checkoutResp.data?.packages && checkoutResp.data.packages.length > 0) {
    // No RSS packages - use the ones we built from checkout
    finalPackages = checkoutResp.data.packages
  }

  response = {
    ...initialData,
    ...rssResp,
    ...checkoutResp,
    data: {
      ...checkoutResp.data,
      ...(rssResp.data || {}),
      packages: finalPackages,
    },
  }

  if (response?.success) {
    rssData.value = response
  } else {
    message.value = response

    // Clear legacy data/order params from URL on failure
    if (!token && (data || order)) {
      replacingUrl = true
      router.replace({
        query: {
          ...route.query,
          data  : undefined,
          order : undefined,
        } })
    }
  }

  loading.value = false
}
</script>

<style lang="scss" scoped>
.rogue-shipping-system-page {
  margin: $space-l 0;
}

.rogue-shipping-system {
  :deep(.spinner-overlay-container) {
    min-height: 500px;
  }
}

.block {
  margin-bottom: $space-s;

  :deep(.container.large) {
    max-width: 800px;
    margin: 0 auto;
    padding: 0 !important; // needed
  }
}

.recommendations {
  margin-bottom: $space-xxxxl;
}

.modal {
  &.is-error:deep() {
    .modal-container {
      border: $border-width-medium solid $color-red;
    }
  }

  :deep() {
    .modal-close-button .icon {
      width: 14px;
      height: 14px;
    }

    .modal-body {
      padding: $space-xl;
    }
  }
}
</style>
